import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '../components/FormField';
import { Captcha } from '../components/Captcha';
import { StepActions } from '../components/StepActions';
import { Alert } from '../components/Alert';
import { addressLookup, RegistrationApiError } from '../../../../services/registrationApi';
import { isValidZipCode, maskZipCode, onlyDigits } from '../utils';
import type { AddressLookupResult } from '../types';

interface AddressStepProps {
  initialZip?: string;
  initialNumber?: string;
  onComplete: (result: AddressLookupResult) => void;
}

/** Passo 1: consulta de endereço por CEP + número, protegida por captcha. */
export function AddressStep({ initialZip = '', initialNumber = '', onComplete }: AddressStepProps) {
  const { t } = useTranslation();
  const [zip, setZip] = useState(initialZip);
  const [number, setNumber] = useState(initialNumber);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ zip?: string; number?: string }>({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const errs: { zip?: string; number?: string } = {};
    if (!isValidZipCode(zip)) errs.zip = t('views.registration.address.invalid_zip', 'Informe um CEP válido (8 dígitos).');
    if (!number.trim()) errs.number = t('views.registration.address.invalid_number', 'Informe o número do endereço.');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    if (!captchaToken) {
      setError(t('views.registration.captcha_required', 'Confirme que você não é um robô.'));
      return;
    }

    setSubmitting(true);
    try {
      const result = await addressLookup({
        zipCode: onlyDigits(zip),
        number: number.trim(),
        captchaToken,
      });
      onComplete(result);
    } catch (err) {
      const message =
        err instanceof RegistrationApiError && err.status === 404
          ? t('views.registration.address.not_found', 'Não encontramos um endereço com medidor Laager para esse CEP e número. Confira os dados.')
          : err instanceof RegistrationApiError
            ? err.message
            : t('views.registration.address.lookup_error', 'Não foi possível consultar o endereço. Tente novamente.');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <FormField
        label={t('views.registration.address.zip_label', 'CEP')}
        icon="fa-map-marker"
        inputMode="numeric"
        autoComplete="postal-code"
        placeholder="00000-000"
        value={zip}
        onChange={(e) => setZip(maskZipCode(e.target.value))}
        error={fieldErrors.zip}
        maxLength={9}
        required
        autoFocus
      />

      <FormField
        label={t('views.registration.address.number_label', 'Número')}
        icon="fa-hashtag"
        placeholder={t('views.registration.address.number_placeholder', 'Ex.: 123')}
        value={number}
        onChange={(e) => setNumber(e.target.value)}
        error={fieldErrors.number}
        maxLength={20}
        required
      />

      <Captcha onChange={setCaptchaToken} />

      <StepActions submitLabel={t('views.registration.address.submit', 'Buscar endereço')} loading={submitting} loadingLabel={t('views.registration.address.submitting', 'Buscando...')} />
    </form>
  );
}
